import { useState } from "react"
import { useTranslation } from "react-i18next"

/**
 * OKS-931 — 48×48 product thumbnail, hotlinked from Shopify's CDN (PRD D13).
 *
 * ## The `width=200` merge happens HERE, not in the database
 *
 * The controller sends the RAW payload URL. Storing the derived thumbnail URL
 * would freeze a Shopify CDN query parameter into every row and need a data
 * backfill when it changes — the `notifications.target_url` lesson, one
 * project later. The merge falls back to the raw URL if it fails, so a
 * Shopify-side change degrades to "the image is big", not "the image is
 * gone".
 *
 * ## The explicit dimensions are load-bearing
 *
 * Without `width`/`height`, 25 hotlinked images reflow the whole list as they
 * arrive. They stay 48×48 at every width: growing them on mobile costs a
 * title line, and the title is what the brand is scanning for (UX §4).
 *
 * ⚠️ `cdn.shopify.com` must be in the CSP `img-src` allowlist. It is
 * report-only today, so omitting it renders fine and merely logs — and the
 * OKS-832 enforce-flip would then blank this page with no other symptom.
 */
function thumbnailUrl(raw: string): string {
  try {
    const url = new URL(raw)
    url.searchParams.set("width", "200")
    return url.toString()
  } catch {
    // A relative or malformed URL: hotlink it unchanged rather than dropping
    // the image. A big image beats no image.
    return raw
  }
}

export default function ProductThumbnail({
  src,
  alt,
  hotlinked = true,
}: {
  src: string | null
  alt: string
  /**
   * OKS-932 — true for a Shopify CDN URL, false for a manual product's own
   * Active Storage representation.
   *
   * The `?width=200` merge above is a *Shopify* transform. Appending it to
   * our own representation path is meaningless — the size is already baked
   * into the `:thumb` variation key server-side — so it would be a query
   * parameter that reads as load-bearing and is not. Defaults to true because
   * every pre-OKS-932 caller is a synced row.
   */
  hotlinked?: boolean
}) {
  const { t } = useTranslation()
  const [failed, setFailed] = useState(false)

  // A product with no image renders the placeholder directly. The placeholder
  // is `aria-label`-ed rather than given an empty `alt`, because its absence
  // is information (this product has no image) rather than decoration (UX §6).
  if (src === null || failed) {
    return (
      <div
        className="bg-muted size-12 shrink-0 rounded"
        role="img"
        aria-label={t("settings.brand.products.image_missing")}
        data-testid="brand-product-thumbnail-placeholder"
      />
    )
  }

  return (
    <img
      src={hotlinked ? thumbnailUrl(src) : src}
      alt={alt}
      width={48}
      height={48}
      loading="lazy"
      className="size-12 shrink-0 rounded object-cover"
      data-testid="brand-product-thumbnail"
      onError={() => setFailed(true)}
    />
  )
}
